import React, { useEffect, useState } from "react";
import { Alert, Badge, Card, Col, Placeholder, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, useParams } from "react-router-dom";

import DstTabs from "../../components/DstTabs/DstTabs";
import InfoRow from "../../components/InfoRow/InfoRow";
import { useSession } from "../../contexts/SessionContext";
import { formatDateHumanly } from "../../services/TimeService";
import translate from "../../services/Translate";
import { EphemeralExerciseDetails } from "./EphemeralExercise.d";
import { fetchEphemeralExercise } from "./EphemeralExercise.function";
import "./EphemeralExercise.scss";

const EphemeralExercise = () => {
  const { exercise_id } = useParams();
  const { language } = useSession();
  const [exercise, setExercise] = useState<EphemeralExerciseDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);

  useEffect(() => {
    if (!exercise_id) {
      setExercise(null);
      setFetchError(false);
      setLoading(false);
      return;
    }
    fetchEphemeralExercise(exercise_id, setExercise, setLoading, language, setFetchError);
  }, [exercise_id, language]);

  return (
    <div className="container-fluid mt-3 mb-5 px-3 px-md-4">
      <Helmet>
        <title>QCM Exercise - {exercise_id}</title>
      </Helmet>
      <div className="d-flex align-items-center mb-3">
        <Link
          to="/ephemeral/exercises"
          className="p-2 medium-gray-background black-font rounded me-2 text-decoration-none"
          aria-label={translate(language, "PAGES.EPHEMERAL_EXERCISE.BACK")}
          title={translate(language, "PAGES.EPHEMERAL_EXERCISE.BACK")}
        >
          <span className="dst-icon-chevron-left" aria-hidden="true" />
        </Link>
        <h1 className="mb-0 h3 me-3">{translate(language, "PAGES.EPHEMERAL_EXERCISE.TITLE")}</h1>
      </div>
      {loading ? (
        <Placeholder
          as="div"
          animation="glow"
          aria-label={translate(language, "PAGES.EPHEMERAL_EXERCISE.LOADING")}
          role="status"
        >
          <Placeholder xs={12} className="mb-2" />
          <Placeholder xs={10} className="mb-2" />
          <Placeholder xs={8} />
        </Placeholder>
      ) : fetchError ? (
        <Alert variant="danger">{translate(language, "PAGES.EPHEMERAL_EXERCISE.ERROR")}</Alert>
      ) : !exercise ? (
        <Alert variant="warning">{translate(language, "PAGES.EPHEMERAL_EXERCISE.NOT_FOUND")}</Alert>
      ) : (
        <DstTabs
          activeKey="main-infos"
          onSelect={() => {}}
          variant="primary"
          tabsData={[
            {
              id: "main-infos",
              title: (
                <React.Fragment>
                  <span className="dst-icon-settings-04 me-2" />
                  {translate(language, "PAGES.EXERCISE.TABS.MAIN_INFOS")}
                </React.Fragment>
              ),
              content: (
                <React.Fragment>
                  <div className="m-bold mb-4">
                    {translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.TITLE")}
                  </div>
                  <Row>
                    <Col md={6}>
                      {InfoRow(
                        "dst-icon-hash-01",
                        translate(language, "PAGES.EPHEMERAL_EXERCISES.ID"),
                        <span className="text-break">{exercise.ID}</span>
                      )}
                      {InfoRow(
                        "dst-icon-tag-01",
                        translate(language, "PAGES.EPHEMERAL_EXERCISES.TOPICS"),
                        exercise.topic?.join(", ") || "—"
                      )}
                      {InfoRow(
                        "dst-icon-calendar-date",
                        translate(language, "PAGES.EPHEMERAL_EXERCISES.CREATED_AT"),
                        formatDateHumanly(language, new Date(exercise.dt_created * 1000).toISOString(), true)
                      )}
                      {InfoRow(
                        "dst-icon-info-circle",
                        translate(language, "PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.FIELDS.STATUS"),
                        <Badge bg={exercise.is_deprecated ? "warning" : "success"}>
                          {translate(
                            language,
                            `PAGES.EXERCISE.MAIN_INFOS_TAB.GENERAL.VALUES.${
                              exercise.is_deprecated ? "DEPRECATED" : "ACTIVE"
                            }`
                          )}
                        </Badge>
                      )}
                    </Col>
                    <Col md={6}>
                      {InfoRow(
                        "dst-icon-bar-chart-01",
                        translate(language, "PAGES.EPHEMERAL_EXERCISE.CALLED"),
                        exercise.called
                      )}
                      {InfoRow(
                        "dst-icon-check",
                        translate(language, "PAGES.EPHEMERAL_EXERCISES.SUCCEEDED"),
                        exercise.succeeded
                      )}
                      {InfoRow(
                        "dst-icon-list",
                        translate(language, "PAGES.EPHEMERAL_EXERCISE.MULTIPLE_SOLUTIONS"),
                        translate(
                          language,
                          exercise.display_multiple_solution
                            ? "PAGES.EPHEMERAL_EXERCISE.YES"
                            : "PAGES.EPHEMERAL_EXERCISE.NO"
                        )
                      )}
                    </Col>
                  </Row>
                  <Card className="mt-4 overflow-hidden rounded-4">
                    <Card.Body className="p-3 p-md-4 p-xl-5">
                      <Row className="g-4 g-xl-5">
                        <Col lg={6}>
                          <section>
                            <h2 className="s-regular text-muted mb-3">
                              {translate(language, "PAGES.EPHEMERAL_EXERCISE.STATEMENT")}
                            </h2>
                            <p className="m-regular text-pre-wrap text-break lh-base mb-0">{exercise.resume}</p>
                            {!!exercise.illustrations?.length && (
                              <div className="d-flex flex-column gap-2 mt-3">
                                {exercise.illustrations.map((url, index) => (
                                  <figure key={index} className="border rounded-3 p-2 mb-0 text-center">
                                    <img
                                      src={url}
                                      alt={`${translate(language, "PAGES.EPHEMERAL_EXERCISE.ILLUSTRATION")} ${
                                        index + 1
                                      }`}
                                      className="ephemeral-exercise-illustration img-fluid rounded"
                                    />
                                  </figure>
                                ))}
                              </div>
                            )}
                          </section>
                        </Col>
                        <Col lg={6}>
                          <section>
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <h2 className="s-regular text-muted mb-0">
                                {translate(language, "PAGES.EPHEMERAL_EXERCISE.ANSWERS")}
                              </h2>
                              <span className="badge rounded-pill medium-gray-background text-body">
                                {exercise.answers?.length || 0}
                              </span>
                            </div>
                            {exercise.answers?.length ? (
                              <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
                                {exercise.answers.map((answer, index) => (
                                  <li
                                    key={index}
                                    className={`border rounded-3 px-3 py-2 ${
                                      answer.solution ? "border-success bg-success-subtle" : ""
                                    }`}
                                  >
                                    <div className="d-flex align-items-center gap-2">
                                      <span
                                        className={`badge rounded-2 px-2 py-1 flex-shrink-0 ${
                                          answer.solution ? "bg-success text-white" : "medium-gray-background text-body"
                                        }`}
                                      >
                                        {answer.label?.trim() ||
                                          (index < 26 ? String.fromCharCode(65 + index) : index + 1)}
                                      </span>
                                      <div className="d-flex align-items-center flex-wrap gap-1 gap-sm-2 flex-grow-1">
                                        <p className="text-pre-wrap text-break lh-base m-regular mb-0 flex-grow-1">
                                          {answer.text}
                                        </p>
                                        {answer.solution && (
                                          <div className="d-flex align-items-center gap-1 text-success-emphasis small">
                                            <span className="dst-icon-check" aria-hidden="true" />
                                            {translate(language, "PAGES.EPHEMERAL_EXERCISE.CORRECT_ANSWER")}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <Alert variant="info" className="mb-0">
                                {translate(language, "PAGES.EPHEMERAL_EXERCISE.NO_ANSWERS")}
                              </Alert>
                            )}
                          </section>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </React.Fragment>
              ),
            },
          ]}
        />
      )}
    </div>
  );
};

export default EphemeralExercise;
